import {
  Button,
  Divider,
  EButtonColorVariant,
  EHelpTextVariant,
  HelpText,
  TabBar,
} from '@outdoorsyco/bonfire';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import TripsLight from '@/assets/trips/Trips-Light.svg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import HeadMeta from '@/components/static-pages/HeadMeta';
import SwitchbackHeading from '@/components/switchback/Heading/Heading';
import Loading from '@/components/switchback/Loading/Loading';
import BidMatchCardContainer from '@/components/trips/BidMatchCard/BidMatchCardContainer';
import BookingCardContainer from '@/components/trips/BookingCard/BookingCardContainer';
import { ClaimsGuideModal } from '@/components/trips/ClaimsGuide';
import { TripGuideContainer } from '@/components/trips/TripGuideContainer';
import { PageEmptyState } from '@/components/ui/PageEmptyState';
import { EBookingsFilters } from '@/constants/booking';
import { useBids } from '@/hooks/useBids';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { fetchBookings } from '@/redux/modules/bookings/actions';
import { TRootState } from '@/redux/rootReducer';
import { trackSmartMatchSimilarSearchClickedEvent } from '@/services/analytics/trips';
import { getBidSearchHref, getSimilarSearchBid } from '@/utility/getBidSearchHref';
import { getParamAsString } from '@/utility/queryParams';
import { triggerLoginModal } from '@/utility/triggerLoginModal';

enum ETripsFilter {
  UPCOMING = 'upcoming',
  INACTIVE = 'inactive',
  SMART_MATCH = 'smart_match',
}

const upcomingTripsSelector = (state: TRootState) => [
  ...(state.bookings.bookings[EBookingsFilters.PENDING]?.data || []),
  ...(state.bookings.bookings[EBookingsFilters.UPCOMING]?.data || []),
];

const inactiveTripsSelector = (state: TRootState) => [
  ...(state.bookings.bookings[EBookingsFilters.COMPLETED]?.data || []),
  ...(state.bookings.bookings[EBookingsFilters.CANCELED]?.data || []),
  ...(state.bookings.bookings[EBookingsFilters.ABANDONED]?.data || []),
];

const tripsErrorSelector = (state: TRootState) =>
  Object.keys(state.bookings.bookings).some(v => {
    return state.bookings.bookings?.[v]?.error;
  });

const loadingTripsSelector = (state: TRootState) =>
  !Object.keys(state.bookings.bookings).length ||
  Object.keys(state.bookings.bookings).some(v => {
    return state.bookings.bookings?.[v]?.isFetching;
  });

const EmptyState = () => (
  <PageEmptyState
    icon={<TripsLight className="h-auto w-[180px] lg:w-auto" />}
    title={<FormattedMessage defaultMessage="No trips yet" id="VrAdhd" />}
    description={
      <FormattedMessage
        defaultMessage="This page feels lonely. Let’s plan your next adventure."
        id="zqRheX"
      />
    }
  />
);

const TripsInnerContent = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const router = useRouter();
  const queryParams = useQueryParams();
  const { locale } = useLocaleSettings();

  const isAuthenticated = useSelector(selectAuthenticated);

  const upcomingTrips = useSelector(upcomingTripsSelector);
  const inactiveTrips = useSelector(inactiveTripsSelector);

  const tripsError = useSelector(tripsErrorSelector);
  const loadingTrips = useSelector(loadingTripsSelector);

  const showSmartMatch = !!process.env.enableSmartMatch;
  const {
    activeBids,
    isLoading: loadingBids,
    revalidate: revalidateBids,
  } = useBids(showSmartMatch && isAuthenticated);

  const queryFilter = getParamAsString(queryParams.filter);
  const filter =
    queryFilter && Object.values(ETripsFilter).includes(queryFilter as ETripsFilter)
      ? (queryFilter as ETripsFilter)
      : ETripsFilter.UPCOMING;

  const hasSomeNonStationaryRVBookings = upcomingTrips.some(booking => {
    return booking.rental_summary?.rental_category === 'rv' && !booking.delivery.stationary;
  });

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        const types = ['rv', 'stay', 'campground'];

        await Promise.all([
          dispatch(fetchBookings(EBookingsFilters.UPCOMING, types, locale)),
          dispatch(fetchBookings(EBookingsFilters.PENDING, types, locale)),
          dispatch(fetchBookings(EBookingsFilters.ABANDONED, types, locale)),
          dispatch(fetchBookings(EBookingsFilters.COMPLETED, types, locale)),
          dispatch(fetchBookings(EBookingsFilters.CANCELED, types, locale)),
        ]);
      }
    })();
  }, [isAuthenticated, dispatch, locale]);

  useEffect(() => {
    if (!isAuthenticated) {
      triggerLoginModal(undefined, false);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <section>
        <MaxWidth className="mx-auto my-8">
          <EmptyState />
        </MaxWidth>
      </section>
    );
  }

  if (tripsError) {
    return (
      <HelpText
        className="px-4 py-2 m-2"
        text={intl.formatMessage({
          defaultMessage: "It looks like we're having some issues. Please try again shortly.",
          id: 'hcZJ/o',
        })}
        variant={EHelpTextVariant.Error}
        showIcon
      />
    );
  }

  if (loadingTrips) {
    return <Loading />;
  }

  if (!upcomingTrips?.length && !inactiveTrips?.length) {
    return (
      <section>
        <MaxWidth className="mx-auto my-8">
          <EmptyState />
        </MaxWidth>
      </section>
    );
  }

  const tabItems = [
    {
      title: intl.formatMessage({ defaultMessage: 'Upcoming', id: '6/aEFZ' }),
      filter: ETripsFilter.UPCOMING,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Inactive', id: '6Tps09' }),
      filter: ETripsFilter.INACTIVE,
    },
    ...(showSmartMatch
      ? [
          {
            title: intl.formatMessage({
              id: 'HmnPJ/',
              defaultMessage: 'Smart Match',
              description: 'Trips > Smart Match tab label',
            }),
            filter: ETripsFilter.SMART_MATCH,
          },
        ]
      : []),
  ];

  const title = intl.formatMessage({
    defaultMessage: 'Trips',
    id: 'XmO9g/',
  });

  const bidsWithMatches = activeBids.filter(bid => bid.bid_matches?.length);
  const similarSearchBid = getSimilarSearchBid(activeBids);

  const renderSmartMatchContent = () => {
    if (loadingBids) {
      return <Loading />;
    }

    if (bidsWithMatches.length) {
      return bidsWithMatches.map(bid => (
        <BidMatchCardContainer key={bid.id} bid={bid} onBidMatchDeclined={revalidateBids} />
      ));
    }

    if (similarSearchBid) {
      return (
        <PageEmptyState
          icon={<TripsLight className="h-auto w-[180px] lg:w-auto" />}
          title={
            <FormattedMessage
              id="+/5g2E"
              defaultMessage="No matches found"
              description="Trips > Smart Match tab > Empty state title when bid exists but no matches were found"
            />
          }
          description={
            <FormattedMessage
              id="BRCKOU"
              defaultMessage="We couldn't find any RVs that match your dates and preferences. Try searching with different dates or adjusting your preferences."
              description="Trips > Smart Match tab > Empty state description when bid exists but no matches were found"
            />
          }
          actions={
            <Button
              className="w-full mt-8 md:w-auto"
              href={getBidSearchHref(similarSearchBid)}
              onClick={() =>
                trackSmartMatchSimilarSearchClickedEvent({ bidID: similarSearchBid.id })
              }
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({
                id: 'onGiKB',
                defaultMessage: 'Search similar RVs',
                description:
                  'Trips > Smart Match tab > No matches empty state > Button that opens search filtered like the trip request',
              })}
            />
          }
        />
      );
    }

    return (
      <PageEmptyState
        icon={<TripsLight className="h-auto w-[180px] lg:w-auto" />}
        title={
          <FormattedMessage
            id="AojeNS"
            defaultMessage="No matches right now"
            description="Trips > Smart Match tab > Empty state title when no matches are available"
          />
        }
        description={
          <FormattedMessage
            id="2uXPoi"
            defaultMessage="Check back later to see if we've matched you with RV hosts whose vehicles fit your search preferences. When we do, you can view their listings and book your perfect RV."
            description="Trips > Smart Match tab > Empty state description explaining to check back for matches"
          />
        }
      />
    );
  };

  return (
    <>
      <section>
        <MaxWidth className="mx-auto my-8 md:px-8" collapseMobile>
          <div className="mx-4 md:mx-0">
            <SwitchbackHeading
              level={2}
              className="mb-8 font-medium text-gray-900 font-highlight autoType800 md:mx-0">
              {title}
            </SwitchbackHeading>

            <TabBar className="mb-8" ariaLabel={title}>
              {tabItems.map(item => (
                <TabBar.Item
                  key={item.filter}
                  label={item.title}
                  active={filter === item.filter}
                  onClick={() => {
                    router.push(`/trips?filter=${item.filter}`, undefined);
                  }}
                />
              ))}
            </TabBar>
          </div>

          {filter === ETripsFilter.UPCOMING &&
            (upcomingTrips.length ? (
              <>
                <BookingCardContainer bookings={upcomingTrips} />

                {hasSomeNonStationaryRVBookings && (
                  <div className="ml-4 md:ml-0">
                    <Divider className="mb-8" />
                    <TripGuideContainer />
                  </div>
                )}
              </>
            ) : (
              <EmptyState />
            ))}

          <div className="mx-4 md:mx-0">
            {filter === ETripsFilter.INACTIVE &&
              (inactiveTrips.length ? (
                <BookingCardContainer bookings={inactiveTrips} />
              ) : (
                <div className="my-8 text-center">
                  <FormattedMessage defaultMessage="No inactive trips" id="S0jQkJ" />
                </div>
              ))}

            {filter === ETripsFilter.SMART_MATCH && renderSmartMatchContent()}
          </div>
        </MaxWidth>
      </section>

      <ClaimsGuideModal />
    </>
  );
};

export const TripsIndex = () => {
  const intl = useIntl();

  return (
    <>
      <HeadMeta
        title={intl.formatMessage({
          defaultMessage: 'Upcoming Trips',
          id: 'y1y+Ik',
        })}
        noindex
      />

      <TripsInnerContent />
    </>
  );
};
